import { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/molecules/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/molecules/Tabs';
import { Select } from '@/components/atoms/Select';
import { Spinner } from '@/components/atoms/Spinner';
import { Badge } from '@/components/atoms/Badge';
import { useAsyncData } from '@/hooks/useAsyncData';
import {
  analyticsPreferencesAPI,
  type PreferencesAnalyticsRange,
  type PreferencesAnalyticsResponse,
} from '@/services/api/analyticsPreferences.api';
import { getCompanies } from '@/services/api/backoffice.api';
import { localizedField } from '@/utils/localizedField';
import type { EnglishLevel } from '@/types/story.types';
import styles from './PreferencesAnalyticsPage.module.scss';

const RANGES: PreferencesAnalyticsRange[] = ['week', 'month', 'all'];
const LEVELS: EnglishLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
const RANGE_LABEL: Record<PreferencesAnalyticsRange, string> = {
  week: 'Semanal',
  month: 'Mensual',
  all: 'Histórico',
};

/**
 * Backoffice analytics for declared learning preferences.
 * Tabs (semanal/mensual/histórico) + filtros opcionales por nivel CEFR y empresa
 * + barras de top categorías + panel de gap analysis (demanda vs stories
 * disponibles).
 *
 * Nota: el backoffice no monta i18n provider, así que todos los strings van
 * en español hardcoded (mismo enfoque que el resto del backoffice). Los
 * nombres de categoría sí se localizan porque ya vienen de BD en EN/ES via
 * `localizedField`.
 */
export function PreferencesAnalyticsPage() {
  // Radix Select.Item no permite value="" → usamos el sentinel "all" en la UI
  // y lo traducimos a undefined al llamar al backend.
  const [range, setRange] = useState<PreferencesAnalyticsRange>('all');
  const [level, setLevel] = useState<EnglishLevel | 'all'>('all');
  const [companyId, setCompanyId] = useState<string>('all');

  const companiesQuery = useAsyncData({ fetchFn: () => getCompanies() });

  const data = useAsyncData<PreferencesAnalyticsResponse>({
    fetchFn: () =>
      analyticsPreferencesAPI.get({
        range,
        level: level === 'all' ? undefined : level,
        companyId: companyId === 'all' ? undefined : companyId,
      }),
    dependencies: [range, level, companyId],
  });

  const chartData = useMemo(() => {
    if (!data.data) return [];
    return data.data.items.slice(0, 15).map((item) => ({
      name: localizedField(item, 'name', 'es'),
      users: item.users,
      percentage: item.percentage,
    }));
  }, [data.data]);

  const maxStories = useMemo(() => {
    return Math.max(1, ...(data.data?.items ?? []).map((i) => i.storiesAvailable));
  }, [data.data]);

  return (
    <div className={styles.page}>
      <div className={styles.page__header}>
        <h1 className={styles.page__title}>Preferencias de aprendizaje</h1>
        <p className={styles.page__subtitle}>
          Categorías que los alumnos han declarado durante el onboarding. Úsalas para decidir
          qué contenido crear a continuación.
        </p>
      </div>

      <Tabs value={range} onValueChange={(v) => setRange(v as PreferencesAnalyticsRange)}>
        <TabsList>
          {RANGES.map((r) => (
            <TabsTrigger key={r} value={r}>
              {RANGE_LABEL[r]}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className={styles.page__filters}>
          <div className={styles.page__filter}>
            <label htmlFor="filter-level">Nivel CEFR</label>
            <Select
              value={level}
              onValueChange={(v) => setLevel(v as EnglishLevel | 'all')}
              options={[
                { value: 'all', label: 'Todos los niveles' },
                ...LEVELS.map((l) => ({ value: l, label: l })),
              ]}
            />
          </div>
          <div className={styles.page__filter}>
            <label htmlFor="filter-company">Empresa</label>
            <Select
              value={companyId}
              onValueChange={(v) => setCompanyId(v)}
              options={[
                { value: 'all', label: 'Todas las empresas' },
                ...(companiesQuery.data?.companies ?? []).map((c) => ({
                  value: c.id,
                  label: c.name,
                })),
              ]}
            />
          </div>
        </div>

        {RANGES.map((r) => (
          <TabsContent key={r} value={r}>
            {data.isLoading ? (
              <div className={styles.page__loading}>
                <Spinner size="lg" aria-label="Cargando" />
              </div>
            ) : !data.data || data.data.items.length === 0 ? (
              <div className={styles.page__empty}>
                <p>Aún no hay preferencias declaradas en este rango.</p>
              </div>
            ) : (
              <div className={styles.page__grid}>
                <Card>
                  <CardHeader>
                    <CardTitle>Top categorías</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={Math.max(320, chartData.length * 32)}>
                      <BarChart data={chartData} layout="vertical" margin={{ left: 16 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" />
                        <YAxis type="category" dataKey="name" width={140} />
                        <Tooltip
                          formatter={(value, name) => {
                            const v = typeof value === 'number' ? value : Number(value ?? 0);
                            return name === 'users'
                              ? [v, 'Usuarios']
                              : [`${v}%`, 'Porcentaje'];
                          }}
                        />
                        <Bar dataKey="users" fill="var(--color-primary-500, #525252)" />
                      </BarChart>
                    </ResponsiveContainer>
                    <Badge variant="secondary">
                      {data.data.totalUsers} usuario(s) en el rango seleccionado
                    </Badge>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Gap analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className={styles.gap__legend}>
                      <span>
                        <span
                          className={`${styles.gap__legendDot} ${styles['gap__legendDot--demand']}`}
                        />
                        Demanda
                      </span>
                      <span>
                        <span
                          className={`${styles.gap__legendDot} ${styles['gap__legendDot--supply']}`}
                        />
                        Stories disponibles
                      </span>
                      <span>
                        <span
                          className={`${styles.gap__legendDot} ${styles['gap__legendDot--warning']}`}
                        />
                        Falta contenido
                      </span>
                    </div>
                    <div className={styles.gap}>
                      {data.data.items.slice(0, 15).map((item) => {
                        const demandPct = item.percentage; // 0-100
                        const supplyPct = (item.storiesAvailable / maxStories) * 100;
                        // Marca como "gap" cuando la demanda es notable (>10%) y
                        // la oferta relativa < demanda relativa.
                        const hasGap = demandPct >= 10 && supplyPct < demandPct;
                        return (
                          <div key={item.categoryId} className={styles.gap__item}>
                            <div className={styles.gap__row}>
                              <span className={styles.gap__name}>
                                {localizedField(item, 'name', 'es')}
                              </span>
                              <span className={styles.gap__numbers}>
                                {item.users} usuarios · {item.percentage}% · {item.storiesAvailable}{' '}
                                stories
                              </span>
                            </div>
                            <div className={styles.gap__bars}>
                              <div className={styles.gap__barWrap}>
                                <div
                                  className={`${styles.gap__bar} ${
                                    hasGap
                                      ? styles['gap__bar--warning']
                                      : styles['gap__bar--demand']
                                  }`}
                                  style={{ width: `${Math.min(100, demandPct)}%` }}
                                />
                              </div>
                              <div className={styles.gap__barWrap}>
                                <div
                                  className={`${styles.gap__bar} ${styles['gap__bar--supply']}`}
                                  style={{ width: `${Math.min(100, supplyPct)}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export default PreferencesAnalyticsPage;
